import React, { FC, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";

import { Box, Stack, Typography } from "@components/base";
import { Logo } from "@components/common";
import { useLoginWithSsoToken } from "@api";
import { useNavigateAfterLogin } from "@hooks";

type Status = "loading" | "success" | "error";

const SsoLoginPage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginWithSsoTokenAsync } = useLoginWithSsoToken();
  const navigateAfterLogin = useNavigateAfterLogin();
  const [status, setStatus] = useState<Status>("loading");

  const handleSsoLogin = async (token: string): Promise<void> => {
    try {
      const response = await loginWithSsoTokenAsync(token);

      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("refresh_token", response.refresh_token);

      window.dispatchEvent(new Event("access_token_change"));

      setStatus("success");

      enqueueSnackbar(t("ssoLogin.success", "SSO login successful"), {
        variant: "success",
        autoHideDuration: 3000,
      });

      // Navigate to the appropriate page after login
      await navigateAfterLogin();
    } catch {
      setStatus("error");
      enqueueSnackbar(t("ssoLogin.error", "SSO login failed"), {
        variant: "error",
        autoHideDuration: 5000,
      });
      navigate("/login");
    }
  };

  useEffect(() => {
    const token = searchParams.get("token");

    if (token) {
      handleSsoLogin(token);
    } else {
      setStatus("error");
      enqueueSnackbar(t("ssoLogin.invalidToken", "Invalid SSO token"), {
        variant: "error",
        autoHideDuration: 5000,
      });
      navigate("/login");
    }
  }, [searchParams]);

  if (status === "loading") {
    return (
      <Stack justifyContent="center" alignItems="center" spacing={3} height="100vh">
        <Box width={160}>
          <Logo type="green0meter-with-csob" />
        </Box>
        <CircularProgress />
        <Typography variant="subtitle1" textAlign="center">
          {t("ssoLogin.processing", "Processing SSO login...")}
        </Typography>
      </Stack>
    );
  }

  if (status === "error") {
    return (
      <Stack justifyContent="center" alignItems="center" spacing={3} height="100vh">
        <Box width={160}>
          <Logo type="green0meter-with-csob" />
        </Box>
        <Typography variant="subtitle1" textAlign="center" color="error">
          {t("ssoLogin.error", "SSO login failed")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack justifyContent="center" alignItems="center" spacing={3} height="100vh">
      <Box width={160}>
        <Logo type="green0meter-with-csob" />
      </Box>
      <Typography variant="subtitle1" textAlign="center">
        {t("ssoLogin.success", "SSO login successful")}
      </Typography>
    </Stack>
  );
};

export default SsoLoginPage;
